import React, { useCallback, useEffect, useState } from "react";
import { observer, useLocalObservable } from "mobx-react-lite";
import { HeroDataInfo, ScreenResponsive, WithdrawResume as WithdrawResumeComponent } from "../../components";
import {
	Box,
	ButtonPrimary,
	ButtonSecondary,
	Checkbox,
	Grid,
	GridItem,
	IconSearchMoneyLight,
	skinVars,
	Text3,
	Text4,
	TextLink,
	useScreenSize,
	IconAddMoreRegular as AddIcon,
	IconShoppingCartRegular as ShoppingCartIcon,
	IconTrashCanRegular as RemoveIcon,
	Text2,
	IconGiftRegular,
} from "@telefonica/mistica";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { strings } from "../../localization";
import { useGlobalStore } from "../../contexts/globalStoreContext";
import { cpfPrivacyMask } from "../../utils/formatters";
import { useAnalytics } from "../../hooks/useAnalytics";
import { WithdrawResumeStore } from "./store";
import { PaymentInfoEnum, TabSelectedEnum } from "../../stores";
import { useNavigateCustom } from "../../hooks";
import SellersCodeModal from "./components/SellersCodeModal";
import { getFFBigPayLuck, getFFFgtsSellersCode } from "../../services/SystemConfiguration";
import { colors } from "../../theme/colors";
import RemoveSellersCodeModal from "./components/RemoveSellersCodeModal";
import { ValidationTypeOTP } from "../../enum";

interface Fields {
	anticipationValue: number;
	anticipationYears: number;
}

const WithdrawResume: React.FC = observer(() => {
	const navigate = useNavigateCustom();

	const { anticipationStore: store, paymentInfo, authenticationStore, validationOTPStore } = useGlobalStore();
	const isTransferByPix = paymentInfo.transferType.value === PaymentInfoEnum.PIX;
	const tabSelected = store.getTabSelected();
	const isPartial = tabSelected === TabSelectedEnum.PARTIAL;
	const partialData = store.partialAnticipationShelf.partialSimulationData;
	const totalData = store.totalAnticipationShelf;

	const { isDesktopOrBigger } = useScreenSize();
	const [isChecked, setChecked] = useState(false);
	const [ffFgtsSellerCodeEnabled, setFfFgtsSellerCodeEnabled] = useState<boolean>(false);
	const [ffBigPayLuck, setFfBigPayLuck] = useState(false);
	const { onOpen: onOpenSellersCodeModal, isOpen: isOpenSellersCodeModal, onClose: onCloseModalCode } = useDisclosure();
	const { onOpen: onOpenRemoveSellersCodeModal, isOpen: isOpenRemoveSellersCodeModal, onClose: onCloseRemoveSellersCodeModal } = useDisclosure();

	const cpf = authenticationStore.currentUser?.documentNumber;

	const { checkbox, sellersCode: { insertSellersCode } } = strings.components.withdrawResume;
	const { title, description, buttonPrimary, buttonSecondary, vivoPayAward } = strings.pages.withdrawResume;
	const { anticipation } = strings.pages.navigation;

	const { addAnalyticEvent, updatePageView } = useAnalytics();
	const withdrawResumeStore = useLocalObservable(() => new WithdrawResumeStore(authenticationStore));
	const sellerIdStore = store.sellerId;
	const onChangeSellerCode = sellerIdStore.onChange;
	const sellerCodeValue = sellerIdStore.field.value;
	const sellerCodeSelected = store.sellerIdSelected.value;

	const renderFields = useCallback((): Fields => {
		if (isPartial) {
			return {
				anticipationValue: partialData.value?.disbursedIssueAmount || 0,
				anticipationYears: partialData.value?.yearsInAdvance || 0,
			};
		}

		return {
			anticipationValue: totalData.disbursedIssueAmount.value,
			anticipationYears: totalData.yearsAdvance.value,
		};
	}, [tabSelected]);

	const renderKeyType = useCallback(() => {
		switch (paymentInfo.pixKeyType.value) {
			case "cpf":
				return "CPF";
			case "email":
				return "E-mail";
			case "random":
				return "Chave aleatória";
			default:
				return "Celular";
		}
	}, [paymentInfo.pixKeyType.value]);

	const handleLinkOnPress = (): void => {
		addAnalyticEvent(
			{ action: "click.link", description: checkbox.labelTerms, step: "fgts:recebimento:pix" },
			{ applySanitize: true },
		);
		navigate("/contract-viewer");
	};

	useEffect(() => {
		updatePageView({
			name: "Vivo Money FGTS Dados Bancários Pix Resumo",
			pagePath: "/fgts/dados-bancarios/pix/resumo",
		});
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 0);
	}, []);

	const handleToHire = async (): Promise<void> => {
		addAnalyticEvent(
			{ action: "click.button", description: buttonPrimary, step: "fgts:recebimento:pix" },
			{ applySanitize: true },
		);

		await withdrawResumeStore.handleSaveConsentingTerms(ffBigPayLuck);
		await validationOTPStore.sendValidationOTP(ValidationTypeOTP.PHONE);

		if (withdrawResumeStore.getIsSavedConsentingTerms) {
			navigate("/sign-contract");
		}
	};

	const fetchFeatureFlag = useCallback(async (): Promise<void> => {
		const [ffSellerCode, ffBigPayLuckValue] = await Promise.all([getFFFgtsSellersCode(), getFFBigPayLuck()]);
		setFfFgtsSellerCodeEnabled(ffSellerCode);
		setFfBigPayLuck(ffBigPayLuckValue);
	}, []);

	useEffect(() => {
		fetchFeatureFlag();
	}, []);

	const onCloseSellersCodeModal = (): void => {
		onCloseModalCode();
		onChangeSellerCode("");
	};

	const onConfirmSellerCode = (): void => {
		store.saveSellerIdSelected(sellerCodeValue);
		onCloseSellersCodeModal();
	};

	const onRemoveSellerCode = (): void => {
		store.saveSellerIdSelected("");
	};

	const onConfirmRemoveSellersCode = (): void => {
		onRemoveSellerCode();
		onCloseRemoveSellersCodeModal();
	};

	const handleAwardTerms = (): void => {
		addAnalyticEvent(
			{ action: "click.link", description: "sorte grande vivo pay", step: "fgts:recebimento:pix" },
			{ applySanitize: true },
		);
		navigate("/contract-vivo-pay-award-viewer");
	};

	return (
		<>
			<ScreenResponsive
				onBack={() => navigate(-1)}
				navigationTitle={anticipation}
				responsiveLayoutProps={{ backgroundColor: "#F5F5F5" }}
			>
				<Box paddingY={32} paddingX={8}>
					<Grid gap={32} columns={{ maxSize: 600 }} justifyContent={"center"}>
						<GridItem alignSelf="center">
							<Box paddingBottom={32}>
								<HeroDataInfo
									icon={<IconSearchMoneyLight size={64} color={skinVars.colors.brand} />}
									title={title}
									description={description}
								/>
							</Box>
							<Grid columns={{ maxSize: 473 }} justifyContent={"center"}>
								<WithdrawResumeComponent
									anticipationValue={renderFields().anticipationValue}
									anticipationYears={renderFields().anticipationYears}
									transferType={isTransferByPix ? "Pix" : "TED"}
									pixKeyType={isTransferByPix ? renderKeyType() : ""}
									pixKey={
										isTransferByPix
											? paymentInfo.pixKeyType.value === "cpf"
												? cpfPrivacyMask(paymentInfo.pixKey.value)
												: paymentInfo.pixKey.value
											: ""
									}
									agency={isTransferByPix ? "" : ""}
									account={isTransferByPix ? "" : ""}
									cpf={cpf || ""}
								/>
								{ffFgtsSellerCodeEnabled && (
									<Box paddingTop={32}>
										{sellerCodeSelected && (
											<>
												<Flex
													justifyContent="space-between"
													px={4}
													pb={6}
													borderBottom={`1px solid ${colors.white[700]}`}
												>
													<Text2 medium color={skinVars.colors.textPrimary}>
														{sellerCodeSelected}
													</Text2>
													<Flex
														onClick={onOpenRemoveSellersCodeModal}
													>
														<RemoveIcon
															size={24} color="#660099" style={{ cursor: "pointer" }} />
													</Flex>
												</Flex>
											</>
										)}
										{!sellerCodeSelected && (
											<Flex
												justifyContent="space-between"
												cursor="pointer"
												onClick={onOpenSellersCodeModal}
											>
												<Flex gap="30px">
													<ShoppingCartIcon size={24} color="#660099" />
													<Text2 medium color={skinVars.colors.textPrimary}>
														{insertSellersCode}
													</Text2>
												</Flex>
												<AddIcon size={24} color="#660099" />
											</Flex>
										)}

									</Box>
								)}
								<Box paddingY={32}>
									<Checkbox
										name="terms-accept"
										checked={isChecked}
										onChange={() => {
											setChecked(!isChecked);
										}}
									>
										{strings.formatString(checkbox.text1)}
										<TextLink onPress={handleLinkOnPress}>{checkbox.labelTerms}</TextLink>
										{checkbox.text2}
									</Checkbox>

									{/* VIVO PAY PROMOTION */}
									{ffBigPayLuck && (
										<Box paddingTop={16} className="box-promotion-pay">
											<Box className="row-promotion-pay">
												<IconGiftRegular color={"black"} size={24} />

												<Text4 weight="medium" color="black">
													{vivoPayAward.title}
												</Text4>
											</Box>

											<Box paddingLeft={32}>
												<Text3 regular color={"#3f3f3f"}>
													{vivoPayAward.description[0]}
												</Text3>

												<TextLink style={{ fontWeight: "400", fontSize: "18px" }} onPress={handleAwardTerms}>
													{` ${vivoPayAward.description[1]} `}
												</TextLink>

												<Text3 regular color={"#3f3f3f"}>
													{vivoPayAward.description[2]}
												</Text3>
											</Box>
										</Box>
									)}
								</Box>
								<Grid columns={isDesktopOrBigger ? 4 : 1} rows={isDesktopOrBigger ? 1 : 2} gap={16}>
									<GridItem columnSpan={isDesktopOrBigger ? 2 : 1} columnStart={isDesktopOrBigger ? 2 : 1}>
										<ButtonPrimary
											onPress={handleToHire}
											disabled={!isChecked}
											style={{ width: isDesktopOrBigger ? "auto" : "100%" }}
										>
											{buttonPrimary}
										</ButtonPrimary>
									</GridItem>
									<GridItem columnSpan={1} columnStart={1} rowStart={isDesktopOrBigger ? 1 : 2}>
										<ButtonSecondary
											onPress={() => {
												addAnalyticEvent(
													{ action: "click.button", description: buttonSecondary, step: "fgts:recebimento:pix" },
													{ applySanitize: true },
												);
												navigate(-1);
											}}
											style={{ width: isDesktopOrBigger ? "auto" : "100%" }}
										>
											<Text3 medium color={skinVars.colors.textButtonSecondary} textAlign="center">
												{buttonSecondary}
											</Text3>
										</ButtonSecondary>
									</GridItem>
								</Grid>
							</Grid>
						</GridItem>
					</Grid>
				</Box>
			</ScreenResponsive>
			<SellersCodeModal
				isOpen={isOpenSellersCodeModal}
				onClose={onCloseSellersCodeModal}
				onConfirm={() => onConfirmSellerCode()}
				value={sellerCodeValue}
				onChange={onChangeSellerCode}
			/>
			<RemoveSellersCodeModal
				isOpen={isOpenRemoveSellersCodeModal}
				onClose={onCloseRemoveSellersCodeModal}
				onConfirm={onConfirmRemoveSellersCode}
			/>
		</>
	);
});

export default WithdrawResume;
